import { COMMON_TEXT_ADMIN } from '@/const/admin/common';
import { FUNDS_EXPENDITURES_TEXT } from '@/const/admin/reports';
import { FundsExpendituresTransactionType, ReportFundsExpendituresRecord } from '@/types/admin/reports';

export type FundsExpendituresAmountValidationTrigger = 'change' | 'blur' | 'save';
export type FundsExpendituresCategoryValidationTrigger = 'change' | 'blur';
export type FundsExpendituresReportingYearValidationTrigger = 'change' | 'blur' | 'save';

interface ValidateFundsExpendituresCategoryParams {
    recordId: number;
    recordType: FundsExpendituresTransactionType;
    categoryId: number | undefined;
    records: ReportFundsExpendituresRecord[];
    trigger?: FundsExpendituresCategoryValidationTrigger;
}

const stripLeadingZeros = (str: string) => str.replace(/^(-?)0+(?=\d)/, '$1');

export const normalizeFundsExpendituresAmountInput = (
    value: string | undefined | null,
    trimEnd = false,
    preserveSpaces = true,
    options?: { whitespaceOnly?: boolean },
): string => {
    if (!value) {
        return '';
    }

    const spaceReplacement = preserveSpaces ? ' ' : '';
    const withNormalizedSpaces = value.replaceAll(/\s+/g, spaceReplacement).trimStart();

    let withCommaSeparator = withNormalizedSpaces.replaceAll('.', ',');
    withCommaSeparator = withCommaSeparator.replace(/^-\s+/, '-');

    const isNegative = withCommaSeparator.startsWith('-');
    const absoluteValue = isNegative ? withCommaSeparator.slice(1) : withCommaSeparator;
    const normalizedPrefix = absoluteValue.startsWith(',') ? `0${absoluteValue}` : absoluteValue;

    withCommaSeparator = isNegative ? `-${normalizedPrefix}` : normalizedPrefix;

    const firstCommaIndex = withCommaSeparator.indexOf(',');

    if (options?.whitespaceOnly) {
        return trimEnd ? withCommaSeparator.trim() : withCommaSeparator;
    }

    if (firstCommaIndex === -1) {
        const noZeros = stripLeadingZeros(withCommaSeparator);
        return trimEnd ? noZeros.trim() : noZeros;
    }

    const rawIntegerPart = withCommaSeparator.slice(0, firstCommaIndex);
    const integerPart = stripLeadingZeros(rawIntegerPart);
    const decimalPart = withCommaSeparator
        .slice(firstCommaIndex + 1)
        .replaceAll(/[\s,]/g, '')
        .slice(0, 2);

    if (trimEnd) {
        return (decimalPart === '' ? integerPart : `${integerPart},${decimalPart}`).trim();
    }

    return `${integerPart},${decimalPart}`;
};

export const validateFundsExpendituresAmount = (
    value: string,
    trigger: FundsExpendituresAmountValidationTrigger = 'change',
): string | undefined => {
    const normalized = normalizeFundsExpendituresAmountInput(value, true);

    if (!normalized) {
        return trigger === 'change' ? undefined : COMMON_TEXT_ADMIN.VALIDATION_MESSAGE.FIELD_REQUIRED;
    }

    const compact = normalized.replaceAll(' ', '');

    if (compact.startsWith('-')) {
        return FUNDS_EXPENDITURES_TEXT.VALIDATION.AMOUNT_NOT_NEGATIVE;
    }

    if (!/^(0|[1-9]\d*)(?:,\d{1,2})?$/.test(compact)) {
        return FUNDS_EXPENDITURES_TEXT.VALIDATION.AMOUNT_ONLY_NUMBER;
    }

    const [integerPart] = compact.split(',');
    if (integerPart.length > 9) {
        return FUNDS_EXPENDITURES_TEXT.VALIDATION.AMOUNT_MAX_DIGITS;
    }

    const parsed = Number.parseFloat(compact.replace(',', '.'));
    if (!Number.isFinite(parsed)) {
        return FUNDS_EXPENDITURES_TEXT.VALIDATION.AMOUNT_ONLY_NUMBER;
    }

    if (parsed < 0) {
        return FUNDS_EXPENDITURES_TEXT.VALIDATION.AMOUNT_NOT_NEGATIVE;
    }

    if (parsed === 0) {
        return trigger === 'change' ? undefined : FUNDS_EXPENDITURES_TEXT.VALIDATION.AMOUNT_NOT_ZERO;
    }

    return undefined;
};

export const validateFundsExpendituresReportingYear = (
    value: string | undefined,
    trigger: FundsExpendituresReportingYearValidationTrigger = 'change',
): string | undefined => {
    if (value !== undefined && value.trim() !== '') {
        return undefined;
    }

    return trigger === 'change' ? undefined : COMMON_TEXT_ADMIN.VALIDATION_MESSAGE.FIELD_REQUIRED;
};

export const validateFundsExpendituresCategory = ({
    recordId,
    recordType,
    categoryId,
    records,
    trigger = 'change',
}: ValidateFundsExpendituresCategoryParams): string | undefined => {
    if (categoryId === undefined) {
        return trigger === 'blur' ? COMMON_TEXT_ADMIN.VALIDATION_MESSAGE.FIELD_REQUIRED : undefined;
    }

    const hasDuplicate = records.some(
        (item) => item.id !== recordId && item.type === recordType && item.categoryId === categoryId,
    );

    if (!hasDuplicate) {
        return undefined;
    }

    // Income duplicate and expense duplicate have the same validation message.
    return FUNDS_EXPENDITURES_TEXT.VALIDATION.CATEGORY_UNIQUE;
};
